/**
 * Tool Compatibility Graph
 * Defines which tools can pass results to which other tools
 * Mirrors backend tool_graph.py
 */

// Tool compatibility mapping
export const TOOL_COMPATIBILITY = {
  subfinder: ['httpx', 'dnsx', 'nuclei', 'ai_assistant', 'waymore'],
  httpx: ['katana', 'nuclei', 'ai_assistant', 'x8', 'ffuf_parallel', 'headless'],
  dnsx: ['httpx', 'ai_assistant', 'waymore'],
  katana: ['nuclei', 'ai_assistant', 'x8', 'ffuf_parallel', 'headless'],
  nuclei: ['ai_assistant'],
  ai_assistant: ['subfinder', 'httpx', 'dnsx', 'katana', 'nuclei'], // Can feed into any tool
  x8: ['ai_assistant', 'httpx', 'nuclei'],
  ffuf_parallel: ['nuclei', 'ai_assistant'],
  headless: ['ai_assistant'],
  manual_input: ['x8'],
  waymore: ['httpx', 'katana', 'nuclei', 'ai_assistant'],
};

// Tool metadata with display information
export const TOOL_METADATA = {
  subfinder: {
    name: 'Subfinder',
    description: 'Subdomain enumeration tool',
    input_type: 'domains',
    output_type: 'domains',
    icon: '🔍',
    color: '#3b82f6',
  },
  httpx: {
    name: 'HTTPx',
    description: 'HTTP probing and analysis',
    input_type: 'domains',
    output_type: 'urls',
    icon: '🌐',
    color: '#10b981',
  },
  katana: {
    name: 'Katana',
    description: 'Web crawler for URLs and endpoints',
    input_type: 'urls',
    output_type: 'urls',
    icon: '🔗',
    color: '#f59e0b',
  },
  dnsx: {
    name: 'DNSx',
    description: 'DNS resolver and lookup',
    input_type: 'domains',
    output_type: 'domains',
    icon: '🖥️',
    color: '#8b5cf6',
  },
  nuclei: {
    name: 'Nuclei',
    description: 'Vulnerability scanner',
    input_type: 'urls',
    output_type: 'vulnerabilities',
    icon: '🛡️',
    color: '#ef4444',
  },
  ai_assistant: {
    name: 'AI Assistant',
    description: 'LLM-powered data processing',
    input_type: 'any',
    output_type: 'structured',
    icon: '🧠',
    color: '#a855f7',
  },
  x8: {
    name: 'Reflected Params',
    description: 'Detect reflected parameters in URLs',
    input_type: 'urls',
    output_type: 'urls',
    icon: '🔍',
    color: '#ec4899',
  },
  ffuf_parallel: {
    name: 'Search Critical Info',
    description: 'Search critical info in files',
    input_type: 'urls',
    output_type: 'vulnerabilities',
    icon: '🔍',
    color: '#f97316',
  },
  headless: {
    name: 'Bruteforce Logins',
    description: 'Bruteforce login forms',
    input_type: 'urls',
    output_type: 'vulnerabilities',
    icon: '🛡️',
    color: '#dc2626',
  },
  waymore: {
    name: 'Waymore',
    description: 'URL из веб-архивов (Wayback, CommonCrawl)',
    input_type: 'domains',
    output_type: 'urls',
    icon: '📦',
    color: '#06b6d4',
  },
};

/**
 * Get list of tools compatible with source tool
 */
export function getCompatibleTools(sourceTool) {
  return TOOL_COMPATIBILITY[sourceTool] || [];
}

/**
 * Check if two tools can be connected
 */
export function canConnect(sourceTool, targetTool) {
  const compatible = TOOL_COMPATIBILITY[sourceTool] || [];
  return compatible.includes(targetTool);
}

/**
 * Get metadata for a specific tool
 */
export function getToolMetadata(toolName) {
  return TOOL_METADATA[toolName] || null;
}

/**
 * Get all available tools
 */
export function getAllTools() {
  return Object.keys(TOOL_METADATA);
}

/**
 * Get input type for a tool
 */
export function getInputType(toolName) {
  const metadata = TOOL_METADATA[toolName];
  return metadata ? metadata.input_type : null;
}

/**
 * Get output type for a tool
 */
export function getOutputType(toolName) {
  const metadata = TOOL_METADATA[toolName];
  return metadata ? metadata.output_type : null;
}

/**
 * Common flag suggestions for each tool
 */
export const TOOL_FLAG_SUGGESTIONS = {
  subfinder: [
    { key: 'timeout', value: '30', description: 'Timeout in seconds' },
    { key: 'max-time', value: '10', description: 'Maximum time in minutes' },
    { key: 'recursive', value: 'true', description: 'Enable recursive subdomain enumeration' },
    { key: 'all', value: 'true', description: 'Use all sources for enumeration' },
  ],
  httpx: [
    { key: 'threads', value: '50', description: 'Number of threads' },
    { key: 'timeout', value: '10', description: 'Timeout in seconds' },
    { key: 'follow-redirects', value: 'true', description: 'Follow HTTP redirects' },
    { key: 'status-code', value: 'true', description: 'Display status code' },
    { key: 'tech-detect', value: 'true', description: 'Display technology in use' },
    { key: 'title', value: 'true', description: 'Display page title' },
  ],
  katana: [
    { key: 'depth', value: '3', description: 'Maximum depth to crawl' },
    { key: 'js-crawl', value: 'true', description: 'Enable JavaScript file crawling' },
    { key: 'headless', value: 'true', description: 'Enable headless hybrid crawling' },
    { key: 'timeout', value: '10', description: 'Timeout in seconds' },
    { key: 'concurrency', value: '10', description: 'Number of concurrent requests' },
  ],
  dnsx: [
    { key: 'resolver', value: '8.8.8.8', description: 'Custom DNS resolver' },
    { key: 'retry', value: '2', description: 'Number of retries' },
    { key: 'timeout', value: '5', description: 'Timeout in seconds' },
    { key: 'threads', value: '100', description: 'Number of threads' },
  ],
  nuclei: [
    { key: 'severity', value: 'critical,high', description: 'Filter by severity' },
    { key: 'tags', value: 'cve', description: 'Filter by tags' },
    { key: 'timeout', value: '5', description: 'Timeout in seconds' },
    { key: 'bulk-size', value: '25', description: 'Number of targets per template' },
    { key: 'concurrency', value: '25', description: 'Maximum number of concurrent templates' },
    { key: 'rate-limit', value: '150', description: 'Maximum requests per second' },
  ],
  ai_assistant: [
    { key: 'prompt', value: '', description: 'System prompt for LLM (required)' },
    { key: 'api_key', value: '', description: 'OpenRouter API key (required)' },
    { key: 'model', value: 'openai/gpt-4o', description: 'LLM model to use' },
    { key: 'output_data', value: '', description: 'JSON schema for output validation (optional)' },
    { key: 'chunk_size', value: '', description: 'Items per chunk for processing (optional)' },
    { key: 'temperature', value: '0.0', description: 'LLM temperature 0.0-1.0 (optional)' },
    { key: 'timeout', value: '60', description: 'Request timeout in seconds (optional)' },
    { key: 'max_retries', value: '2', description: 'Maximum retry attempts (optional)' },
  ],
  x8: [
    { key: 'w', value: '', description: 'Wordlist file path (required)' },
    { key: 'timeout', value: '10', description: 'Timeout in seconds' },
    { key: 'threads', value: '10', description: 'Number of threads' },
    { key: 'rate-limit', value: '10', description: 'Rate limit per second' },
  ],
  ffuf_parallel: [
    { key: 'w', value: '', description: 'Wordlist file path in /mnt (required)' },
    { key: 'max-rps', value: '100', description: 'Maximum requests per second' },
    { key: 'per-host-rps', value: '2', description: 'Max requests per host per second' },
    { key: 'timeout', value: '10.0', description: 'Request timeout in seconds' },
    { key: 'retries', value: '3', description: 'Number of retries for failed requests' },
    { key: 'proxy', value: '', description: 'HTTP proxy URL' },
    { key: 'header', value: '', description: 'Custom HTTP headers (format: "Name: Value")' },
    { key: 'user-agent', value: 'UAZ/SCB', description: 'Custom User-Agent' },
    { key: 'ollama', value: 'false', description: 'Enable Ollama AI verification' },
    { key: 'ollama-url', value: 'http://localhost:11434', description: 'Ollama API URL' },
    { key: 'ollama-model', value: 'llama3.2', description: 'Ollama model name' },
    { key: 'ollama-timeout', value: '10.0', description: 'Timeout for Ollama requests' },
    { key: 'ollama-retries', value: '3', description: 'Number of retries for Ollama requests' },
    { key: 'ollama-retry-delay', value: '1.0', description: 'Delay between Ollama retries' },
    { key: 'ollama-chunk-size', value: '10', description: 'Chunk size for post-scan Ollama verification' },
    { key: 'jitter', value: 'false', description: 'Enable random jitter' },
    { key: 'debug-rate-limit', value: 'false', description: 'Enable debug mode showing rate-limit decisions' },
  ],
  headless: [
    { key: 'users', value: '', description: 'Path to users wordlist file in /mnt (required)' },
    { key: 'passwords', value: '', description: 'Path to passwords wordlist file in /mnt (required)' },
    { key: 'proxy', value: '', description: 'URL прокси (например http://127.0.0.1:8080)' },
    { key: 'rate-limit', value: '0', description: 'Ограничение скорости в миллисекундах между запросами' },
    { key: 'parallel', value: '1', description: 'Количество хостов для параллельной обработки' },
    { key: 'form-discovery-workers', value: '3', description: 'Количество потоков для определения форм' },
    { key: 'strategy', value: 'host_first', description: 'Стратегия перебора: host_first или combo_first' },
    { key: 'skip-enum', value: 'false', description: 'Пропустить этап перечисления пользователей' },
    { key: 'sso', value: 'false', description: 'Включить обработку SSO форм' },
    { key: 'allow-redirection', value: 'false', description: 'Разрешить редиректы при обнаружении форм' },
    { key: 'enum-users', value: 'false', description: 'Режим перечисления пользователей' },
    { key: 'check-login', value: 'false', description: 'Режим проверки форм входа' },
    { key: 'find-basic', value: 'false', description: 'Режим поиска Basic аутентификации' },
    { key: 'insecure', value: 'false', description: 'Отключить проверку TLS сертификатов' },
  ],
  waymore: [
    { key: 'mode', value: 'U', description: 'Режим: U (URL), R (ответы), B (оба)' },
    { key: 'limit', value: '5000', description: 'Макс. количество ответов для загрузки' },
    { key: 'filter_codes', value: '', description: 'Фильтровать по HTTP-кодам (напр. 404,500)' },
    { key: 'filter_types', value: '', description: 'Фильтровать по MIME-типам (напр. text/html)' },
    { key: 'from_date', value: '', description: 'Дата начала (напр. 2020 или 202005)' },
    { key: 'to_date', value: '', description: 'Дата окончания' },
  ],
};

